import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Period} from "../../../../core/interfaces/period.interface";
import {map, Observable, startWith, Subscription} from "rxjs";
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import {ActivatedRoute} from "@angular/router";
import {AsyncPipe, NgForOf} from "@angular/common";
import {
  MatAutocomplete,
  MatAutocompleteModule,
  MatAutocompleteTrigger,
  MatOption
} from "@angular/material/autocomplete";
import {MatFormField, MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput, MatInputModule} from "@angular/material/input";
import {MatChipsModule} from "@angular/material/chips";
import {MatIconModule} from "@angular/material/icon";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {TranslateModule} from "@ngx-translate/core";
import {CdkDragDrop, DragDropModule, moveItemInArray} from "@angular/cdk/drag-drop";
import Sortable from 'sortablejs';

@Component({
  selector: 'app-category-products',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    NgForOf,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    CardsBasicComponent,
    TranslateModule,
    DragDropModule
  ],
  templateUrl: './category-products.component.html',
  styleUrl: './category-products.component.css'
})
export class CategoryProductsComponent implements OnInit, OnDestroy {
  categoryData!: any;
  categoryId!: any;
  periodControl = new FormControl();
  productList: any[] = [];
  filteredProducts!: Observable<any[]>;
  isLoading: boolean = false;

  private subscriptions: Subscription = new Subscription();
  @ViewChild('sortableContainer', {static: false}) sortableContainer!: ElementRef;

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    public _route: ActivatedRoute,) {
  }

  /**
   * Initial method called when the component is initialized.
   * It is responsible for calling the required initialization method(s) for the component.
   *
   * @return {void}
   */
  ngOnInit() {
    this.getRouteParams()
  }

  private getRouteParams(): void {
    this._route.params.subscribe(params => {
      this.categoryId = this._route.snapshot.parent?.params['categoryId'];
      this.loadCategoryData()
    });
  }

  private loadCategoryData() {
    this.subscriptions.add(
      this._apiService.fetchCategory(this.categoryId)
        .subscribe({
          next: (res: any) => {
            this.categoryData = res;
            this.initProductData();
          },
          error: (error) => {
            console.error('There was an error fetching the periods', error);
          }
        })
    )
  }

  private initProductData(): void {
    this.subscriptions.add(
      this._apiService.fetchProductList()
        .subscribe({
          next: (res: any) => {
            this.productList = res;
            this.setFilteredProducts();
          },
          error: (error) => {
            console.error('There was an error fetching the periods', error);
          }
        })
    );
  }

  private setFilteredProducts(): void {
    this.filteredProducts = this.periodControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value.translations['en'].title),
      map(title => title ? this._filter(title) : this.filterAvailableProducts())
    );
  }

  private filterAvailableProducts(): any[] {
    const assignedProductIds = this.categoryData.products.map((p: any) => p.product.id);
    return this.productList.filter(product => !assignedProductIds.includes(product.id));
  }

  private _filter(title: string): any[] {
    const filterValue = title.toLowerCase()
    return this.filterAvailableProducts().filter(option => option.translations['en'].title.toLowerCase().includes(filterValue));
  }

  assignProduct(product: any): void {
    this.periodControl.setValue('');

    const newProduct = {
      order: this.categoryData.products.length + 1, // Product ID
      product: {
        id: product.id,
        translations: product.translations
      }
    };

    if (product && product.id && !this.categoryData.products.find((p: any) => p.id === product.id)) {
      //let productIds = this.categoryData.products.map((p: any) => p.product.id);
      //productIds.push(product.id)
      this.categoryData.products.push(newProduct)

      this.updateCategoryProduct();
    }
  }


  unassignProduct(product: any): void {
    const categoryIndex = this.categoryData.products.findIndex((p: any) => p.product.id === product.product.id);

    if (categoryIndex !== -1) {
      this.categoryData.products.splice(categoryIndex, 1);
      this.recalculateProductOrders();
      this.updateCategoryProduct();
    }
  }

  private recalculateProductOrders(): void {
    this.categoryData.products.forEach((product: any, index: number) => {
      product.order = index;
    });
  }

  private updateCategoryProduct(): void {
    this.isLoading = true;

    const productOrdersPayload = this.categoryData.products.map((product: any) => ({
      id: product.product.id, // Product ID
      order: product.order // Product order
    }));


    // Update the category products by sending the full list of product IDs and their order
    this.subscriptions.add(
      this._apiService.updateCategoryProducts(this.categoryData.id, productOrdersPayload)
        .subscribe({
          next: (res: any) => {
            this.loadCategoryData();  // Ensure data is reloaded
            this._snackbarService.show(res.message);
            this.isLoading = false;
          },
          error: (error) => {
            console.error('There was an error updating the product', error);
            this.isLoading = false;
          }
        })
    );
  }


  drop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.categoryData.products, event.previousIndex, event.currentIndex);

    this.categoryData.products.forEach((product: any, index: number) => {
      product.order = index;
    });

    this.updateCategoryProduct();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}
